/**
 * Forced-translation glossary for grocery staples.
 *
 * Azure Translator v3 has no free-text "context" parameter. Its only built-in
 * biasing mechanism is dynamic-dictionary markup
 * (<mstrans:dictionary translation="...">word</mstrans:dictionary>), but that
 * is unusable here: the babel-api relay caps the `Text` field at ~50 characters
 * (returns "TOOLONG"), and the markup wrapper alone exceeds that for a single
 * word. So instead we resolve a small, hand-enumerated set of staples on the
 * client: if the user's input is exactly a glossary word, we return the forced
 * translation (and its correct reading) WITHOUT calling the relay. This is also
 * free and gives the exact romaji we want, which Azure often gets wrong for
 * these.
 *
 * LIMITATION: this only helps the exact (word x target-language) pairs listed
 * below. It does NOT generalise to other words, phrasings, or languages, and it
 * provides no real "this is a shopping list" context. The genuine general
 * solution is an LLM call via the relay (free-text context + romaji in one
 * shot); that is the intended long-term upgrade.
 */

export interface GlossaryEntry {
  // Forced target-language translation.
  translation: string;
  // Optional override for the displayed transliteration/romaji.
  translit?: string;
}

// from -> to -> word (lowercased) -> entry
type GlossaryMap = {
  [from: string]: {
    [to: string]: {
      [word: string]: GlossaryEntry;
    };
  };
};

export const glossary: GlossaryMap = {
  en: {
    ja: {
      milk: { translation: "牛乳", translit: "gyūnyū" },
      egg: { translation: "卵", translit: "tamago" },
      eggs: { translation: "卵", translit: "tamago" },
      // Azure tends to give リーキ (a transliteration) instead of the
      // everyday Japanese leek.
      leek: { translation: "長ねぎ", translit: "naganegi" },
      leeks: { translation: "長ねぎ", translit: "naganegi" },
      // Tofu firmness: Azure loses the distinction; these are the standard names.
      "firm tofu": { translation: "木綿豆腐", translit: "momen-dōfu" },
      "silken tofu": { translation: "絹ごし豆腐", translit: "kinugoshi-dōfu" },
      tofu: { translation: "豆腐", translit: "tōfu" },
      // Often mistranslated to a katakana transliteration.
      "spring onion": { translation: "青ねぎ", translit: "aonegi" },
      "spring onions": { translation: "青ねぎ", translit: "aonegi" },
      coriander: { translation: "パクチー", translit: "pakuchī" },
      cilantro: { translation: "パクチー", translit: "pakuchī" },
      aubergine: { translation: "なす", translit: "nasu" },
      eggplant: { translation: "なす", translit: "nasu" },
      mince: { translation: "ひき肉", translit: "hikiniku" },
      "minced beef": { translation: "牛ひき肉", translit: "gyū-hikiniku" },
    },
  },
  // Reverse direction: typing the Japanese term on the right yields the everyday
  // English word. Canonical English chosen where several map to one Japanese
  // term (egg, coriander, aubergine, leek). No transliteration is shown for the
  // English (left) side, so translit is omitted.
  ja: {
    en: {
      牛乳: { translation: "milk" },
      卵: { translation: "egg" },
      長ねぎ: { translation: "leek" },
      木綿豆腐: { translation: "firm tofu" },
      絹ごし豆腐: { translation: "silken tofu" },
      豆腐: { translation: "tofu" },
      青ねぎ: { translation: "spring onion" },
      パクチー: { translation: "coriander" },
      なす: { translation: "aubergine" },
      ひき肉: { translation: "mince" },
      牛ひき肉: { translation: "minced beef" },
    },
  },
};

// Resolve a forced translation when the whole input is exactly a glossary word
// (case-insensitive, ignoring surrounding whitespace). Returns undefined for
// anything else, so multi-word items like "milk chocolate" still go to Azure.
export function lookupGlossaryExact(
  text: string,
  from: string,
  to: string,
): GlossaryEntry | undefined {
  const words = glossary[from]?.[to];
  if (!words) return undefined;
  return words[text.trim().toLowerCase()];
}
